import React, { useState } from 'react';
import { ClassDetail } from '../../types/models';
import { registerForClass, cancelRegistration, joinWaitlist } from '../../services/classes';

interface Props {
  event: ClassDetail;
  onUpdate: () => void;
}

export const RegistrationButton: React.FC<Props> = ({ event, onUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const reg = event.userRegistration;
  const isFull = event.registeredCount >= (event.capacity ?? 0);

  if (!reg) return null;

  const act = async (action: () => Promise<unknown>, successMsg: string) => {
    setLoading(true);
    setMessage(null);
    try {
      await action();
      setMessage(successMsg);
      onUpdate();
    } catch (err) {
      setMessage((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  if (event.status === 'Cancelled') {
    return <span className="text-red-500 font-medium">Class Cancelled</span>;
  }
  if (event.status === 'Completed') {
    return <span className="text-gray-500">Class Completed</span>;
  }

  return (
    <div className="space-y-2">
      {reg.isRegistered ? (
        <button
          onClick={() => act(() => cancelRegistration(event.eventId), 'Registration cancelled.')}
          disabled={loading}
          className="px-5 py-2 border border-red-300 text-red-600 rounded-lg text-sm hover:bg-red-50 disabled:opacity-50"
        >
          {loading ? 'Cancelling…' : 'Cancel Registration'}
        </button>
      ) : reg.isWaitlisted ? (
        <div>
          <span className="text-yellow-600 font-medium">
            Waitlisted — position #{reg.waitlistPosition}
          </span>
          <button
            onClick={() => act(() => cancelRegistration(event.eventId), 'Removed from waitlist.')}
            disabled={loading}
            className="ml-3 text-sm text-gray-500 underline hover:text-gray-700 disabled:opacity-50"
          >
            {loading ? '…' : 'Leave waitlist'}
          </button>
        </div>
      ) : isFull ? (
        <button
          onClick={() => act(() => joinWaitlist(event.eventId), 'Added to waitlist!')}
          disabled={loading}
          className="px-5 py-2 bg-yellow-500 text-white rounded-lg text-sm font-medium hover:bg-yellow-600 disabled:opacity-50"
        >
          {loading ? 'Joining waitlist…' : 'Join Waitlist'}
        </button>
      ) : (
        <button
          onClick={() => act(() => registerForClass(event.eventId), "You're registered!")}
          disabled={loading}
          className="px-5 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50"
        >
          {loading ? 'Registering…' : 'Register'}
        </button>
      )}
      {message && (
        <p className={`text-sm ${message.includes('!') || message.includes('cancelled') ? 'text-green-600' : 'text-red-500'}`}>
          {message}
        </p>
      )}
    </div>
  );
};
